import { useState } from "react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAddMarketplace, type AddMarketplaceInput } from "@/api/mutations/plugins";
import type { MarketplaceSource } from "@/types";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (id: string) => void;
}

const EMPTY = {
  name: "",
  sourceType: "github" as MarketplaceSource["type"],
  repo: "",
  url: "",
  path: "",
  ref: "",
  autoUpdate: false,
};

export function AddMarketplaceDialog({ open, onOpenChange, onCreated }: Props) {
  const [form, setForm] = useState({ ...EMPTY });
  const [error, setError] = useState<string | null>(null);
  const mutation = useAddMarketplace();
  const set = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }));

  function close(o: boolean) {
    if (!o) {
      setForm({ ...EMPTY });
      setError(null);
    }
    onOpenChange(o);
  }

  function buildSource(): MarketplaceSource | string {
    const ref = form.ref.trim() || undefined;
    switch (form.sourceType) {
      case "github":
        if (!form.repo.trim()) return "GitHub: owner/repo ist erforderlich";
        return { type: "github", repo: form.repo.trim(), ref: ref ?? null };
      case "url":
        if (!form.url.trim()) return "URL ist erforderlich";
        return { type: "url", url: form.url.trim() };
      case "git":
        if (!form.url.trim()) return "Git-URL ist erforderlich";
        return { type: "git", url: form.url.trim(), ref: ref ?? null };
      case "local":
        if (!form.path.trim()) return "Pfad ist erforderlich";
        return { type: "local", path: form.path.trim() };
    }
  }

  function submit() {
    setError(null);
    if (!form.name.trim()) return setError("Name ist erforderlich");
    const source = buildSource();
    if (typeof source === "string") return setError(source);

    const input: AddMarketplaceInput = {
      name: form.name.trim(),
      source,
      autoUpdate: form.autoUpdate,
    };
    mutation.mutate(input, {
      onSuccess: (m) => {
        toast.success(`Marketplace "${m.name}" hinzugefügt — jetzt synchronisieren`);
        onCreated?.(m.id);
        close(false);
      },
      onError: (err) => {
        const msg = (err as Error).message ?? "Fehler";
        setError(msg);
        toast.error(`Hinzufügen fehlgeschlagen: ${msg}`);
      },
    });
  }

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent data-testid="add-marketplace-dialog">
        <DialogHeader>
          <DialogTitle>Marketplace hinzufügen</DialogTitle>
          <DialogDescription>Eine Plugin-Quelle (Claude-Code-Marketplace). Plugins erscheinen nach dem Sync im Katalog.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="mp-name">Name *</Label>
            <Input id="mp-name" value={form.name} onChange={(e) => set({ name: e.target.value })} placeholder="company-tools" className="mono text-[12px]" data-testid="mp-field-name" />
            <p className="text-[11px] text-text3">kebab-case — wird zum Marketplace-Schlüssel (<span className="mono">name@marketplace</span>).</p>
          </div>

          <div className="space-y-1">
            <Label>Quell-Typ</Label>
            <Select value={form.sourceType} onValueChange={(v) => set({ sourceType: v as MarketplaceSource["type"] })}>
              <SelectTrigger data-testid="mp-field-sourcetype"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="github">github</SelectItem>
                <SelectItem value="url">url</SelectItem>
                <SelectItem value="git">git</SelectItem>
                <SelectItem value="local">local</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {form.sourceType === "github" && (
            <div className="space-y-1">
              <Label htmlFor="mp-repo">Repository *</Label>
              <Input id="mp-repo" value={form.repo} onChange={(e) => set({ repo: e.target.value })} placeholder="anthropics/claude-code-plugins" className="mono text-[12px]" data-testid="mp-field-repo" />
            </div>
          )}
          {(form.sourceType === "url" || form.sourceType === "git") && (
            <div className="space-y-1">
              <Label htmlFor="mp-url">{form.sourceType === "git" ? "Git-URL *" : "URL *"}</Label>
              <Input id="mp-url" value={form.url} onChange={(e) => set({ url: e.target.value })} placeholder="https://…" className="mono text-[12px]" data-testid="mp-field-url" />
            </div>
          )}
          {form.sourceType === "local" && (
            <div className="space-y-1">
              <Label htmlFor="mp-path">Pfad *</Label>
              <Input id="mp-path" value={form.path} onChange={(e) => set({ path: e.target.value })} placeholder="/srv/plugins/company" className="mono text-[12px]" data-testid="mp-field-path" />
            </div>
          )}
          {(form.sourceType === "github" || form.sourceType === "git") && (
            <div className="space-y-1">
              <Label htmlFor="mp-ref">Ref <span className="text-text3">(optional — Branch/Tag/SHA)</span></Label>
              <Input id="mp-ref" value={form.ref} onChange={(e) => set({ ref: e.target.value })} placeholder="main" className="mono text-[12px]" />
            </div>
          )}

          <label className="flex items-center gap-2.5 cursor-pointer pt-1">
            <input type="checkbox" checked={form.autoUpdate} onChange={(e) => set({ autoUpdate: e.target.checked })} data-testid="mp-field-autoupdate" />
            <span className="text-[13px] text-text2">Auto-Update (Plugins beim Sync auf die neueste Version pinnen)</span>
          </label>

          {error && <div className="text-xs text-danger" data-testid="mp-error">{error}</div>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => close(false)}>Abbrechen</Button>
          <Button onClick={submit} disabled={mutation.isPending} data-testid="mp-submit">
            {mutation.isPending ? "…" : "Hinzufügen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
